import { formatFileTypes, formatFileSize } from './string'

export interface PhotoUploadErrorDetails {
  supportedTypes: string[]
  maxSizeBytes: number
}

/**
 * Builds a user-friendly error message for photo upload failures.
 *
 * @param details - Details about the error including supported types and max size
 * @param failedCount - The number of files that failed to upload
 * @returns A formatted error message for display to the user
 *
 * @example
 * buildPhotoUploadErrorMessage({
 *   supportedTypes: ['image/jpeg', 'image/png'],
 *   maxSizeBytes: 5242880
 * }, 1)
 * // -> "File not supported. Only JPG or PNG files under 5 MB can be uploaded."
 *
 * buildPhotoUploadErrorMessage({
 *   supportedTypes: ['image/jpeg', 'image/png'],
 *   maxSizeBytes: 5242880
 * }, 2)
 * // -> "Files not supported. Only JPG or PNG files under 5 MB can be uploaded."
 */
export const buildPhotoUploadErrorMessage = (
  details: PhotoUploadErrorDetails,
  failedCount: number
): string => {
  const formattedTypes = formatFileTypes(details.supportedTypes)
  const { value, unit } = formatFileSize(details.maxSizeBytes)

  const fileWord = failedCount === 1 ? 'File' : 'Files'
  return `${fileWord} not supported. Only ${formattedTypes} files under ${value} ${unit} can be uploaded.`
}

/**
 * Builds a user-friendly message describing photo upload requirements.
 *
 * @param details - Details about the requirements including supported types and max size
 * @returns A formatted requirements message for display to the user
 *
 * @example
 * buildPhotoRequirementsMessage({
 *   supportedTypes: ['image/jpeg', 'image/png'],
 *   maxSizeBytes: 5242880
 * })
 * // -> "Accepted formats: JPG, PNG (max 5 MB per file)"
 */
export const buildPhotoRequirementsMessage = (
  details: PhotoUploadErrorDetails
): string => {
  const formattedTypes = formatFileTypes(details.supportedTypes)
  const { value, unit } = formatFileSize(details.maxSizeBytes)
  return `Accepted formats: ${formattedTypes} (max ${value} ${unit} per file)`
}
